import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  getBaseDataTypes,
  getComponentEditList,
  getPrimaryTypes,
  resolvePrimaryType,
  getComponentEditMapping,
  type PrimaryFieldOptionsDto,
  type FieldDesignDto,
  type PrimaryTypeInfoDto,
  type ComponentEditPrimaryTypeMapping,
  type FieldParameterDto
} from '@/api/endpoints/systemMetadata';
import { changePrimaryType } from '@/api/endpoints/tableColumns';
import type { PrimaryTypeChangeRequest, PrimaryTypeChangeResponse, PrimaryTypeChangeOptions } from '@/api/contracts/tableDefinition';

// Typer basert på auto-genererte API-typer
interface ComponentEditInfo {
  componentEdit?: string | null;
  componentEditId?: number;
  label?: string | null;
}

interface DataTypeInfo {
  label?: string | null;
  varSize?: boolean;
  defaultValue?: string | null;
}

// Type alias for parameter types
type FieldParameterType = 'maxLength' | 'precision' | 'scale';

// Helper functions for FieldDesign parameters (moved to outer scope)
function hasParameter (design: FieldDesignDto | null, paramType: FieldParameterType): boolean {
  if (!design?.storage?.parameters) return false;
  return design.storage.parameters.some(p => p.type === paramType);
}

function getParameter (design: FieldDesignDto | null, paramType: FieldParameterType): FieldParameterDto | null {
  if (!design?.storage?.parameters) return null;
  return design.storage.parameters.find(p => p.type === paramType) || null;
}

function needsNumberFormatting (design: FieldDesignDto | null): boolean {
  if (!design) return false;
  // Kun vis number formatting for numeriske typer (Decimal, Percent, Integer, BigInt)
  const numericTypes = ['Decimal', 'Percent', 'Integer', 'BigInt'];
  if (!numericTypes.includes(design.fieldType)) {
    return false;
  }
  return design.edit.numberFormatDecimalPrecision !== null ||
    design.edit.numberFormatShowThousandsSeparator !== null;
}

function needsMaxLength (design: FieldDesignDto | null): boolean {
  return hasParameter(design, 'maxLength');
}

function needsPrecisionScale (design: FieldDesignDto | null): boolean {
  return hasParameter(design, 'precision') && hasParameter(design, 'scale');
}

function getParameterDefault (design: FieldDesignDto | null, paramType: FieldParameterType): number | null {
  const param = getParameter(design, paramType);
  if (!param?.defaultValue) return null;
  return typeof param.defaultValue === 'number' ? param.defaultValue : null;
}

function getParameterRange (design: FieldDesignDto | null, paramType: FieldParameterType): { min?: number, max?: number } | null {
  const param = getParameter(design, paramType);
  if (!param) return null;
  return {
    min: param.minValue ?? undefined,
    max: param.maxValue ?? undefined
  };
}

export const useSystemMetadataStore = defineStore('systemMetadata', () => {
  // State
  const editComponents = ref<Record<string, ComponentEditInfo>>({});
  const baseDataTypes = ref<Record<string, DataTypeInfo>>({});
  const primaryTypes = ref<PrimaryTypeInfoDto[]>([]);
  const componentEditMapping = ref<ComponentEditPrimaryTypeMapping[]>([]);
  const loadingEditComponents = ref(false);
  const loadingBaseDataTypes = ref(false);
  const errorEditComponents = ref<string | null>(null);
  const errorBaseDataTypes = ref<string | null>(null);

  // Cache flags for å spore om data allerede er hentet
  const editComponentsLoaded = ref(false);
  const baseDataTypesLoaded = ref(false);

  // Promise cache for å unngå duplikate samtidige kall
  let editComponentsPromise: Promise<any> | null = null;
  let baseDataTypesPromise: Promise<any> | null = null;
  let primaryTypesPromise: Promise<any> | null = null;
  let componentEditMappingPromise: Promise<any> | null = null;

  // Computed properties for formaterte data
  const formattedBaseDataTypes = computed(() => {
    return Object.entries(baseDataTypes.value).map(([type, config]) => ({
      value: type,
      label: config.label || type
    }));
  });

  const formattedComponentEditList = computed(() => {
    return Object.entries(editComponents.value).map(([type, config]) => ({
      value: type,
      label: config.label || type
    }));
  });

  const transformedComponentEditList = computed(() => {
    return Object.values(editComponents.value).map(component => ({
      value: component.componentEdit || '',
      label: component.label || ''
    }));
  });

  // Actions
  async function fetchEditComponents (forceRefresh = false) {
    // Hvis data allerede er lastet og ikke force refresh, returner eksisterende data
    if (editComponentsLoaded.value && !forceRefresh) {
      return editComponents.value;
    }

    // Hvis det allerede er et pågående kall, returner samme promise
    if (editComponentsPromise && !forceRefresh) {
      return editComponentsPromise;
    }

    loadingEditComponents.value = true;
    errorEditComponents.value = null;

    editComponentsPromise = (async () => {
      try {
        const data = await getComponentEditList();
        editComponents.value = data;
        editComponentsLoaded.value = true;
        return data;
      } catch (e: any) {
        errorEditComponents.value = e.message || 'Feil ved henting av edit components';
        throw e;
      } finally {
        loadingEditComponents.value = false;
        editComponentsPromise = null;
      }
    })();

    return editComponentsPromise;
  }

  async function fetchBaseDataTypes (forceRefresh = false) {
    // Hvis data allerede er lastet og ikke force refresh, returner eksisterende data
    if (baseDataTypesLoaded.value && !forceRefresh) {
      return baseDataTypes.value;
    }

    // Hvis det allerede er et pågående kall, returner samme promise
    if (baseDataTypesPromise && !forceRefresh) {
      return baseDataTypesPromise;
    }

    loadingBaseDataTypes.value = true;
    errorBaseDataTypes.value = null;

    baseDataTypesPromise = (async () => {
      try {
        const data = await getBaseDataTypes();
        baseDataTypes.value = data;
        baseDataTypesLoaded.value = true;
        return data;
      } catch (e: any) {
        errorBaseDataTypes.value = e.message || 'Feil ved henting av base data types';
        throw e;
      } finally {
        loadingBaseDataTypes.value = false;
        baseDataTypesPromise = null;
      }
    })();

    return baseDataTypesPromise;
  }

  async function fetchPrimaryTypes (forceRefresh = false) {
    if (primaryTypes.value.length && !forceRefresh) return primaryTypes.value;
    if (primaryTypesPromise && !forceRefresh) return primaryTypesPromise;
    primaryTypesPromise = (async () => {
      try {
        const data = await getPrimaryTypes();
        primaryTypes.value = data;
        return data;
      } finally {
        primaryTypesPromise = null;
      }
    })();
    return primaryTypesPromise;
  }

  async function fetchComponentEditMapping (forceRefresh = false) {
    if (componentEditMapping.value.length && !forceRefresh) return componentEditMapping.value;
    if (componentEditMappingPromise && !forceRefresh) return componentEditMappingPromise;
    componentEditMappingPromise = (async () => {
      try {
        const data = await getComponentEditMapping();
        componentEditMapping.value = data;
        return data;
      } finally {
        componentEditMappingPromise = null;
      }
    })();
    return componentEditMappingPromise;
  }

  async function resolvePrimary (primaryType: string, options: PrimaryFieldOptionsDto): Promise<FieldDesignDto> {
    return await resolvePrimaryType(primaryType, options);
  }

  /**
   * Utfør primærtype-endring for en kolonne
   */
  async function changePrimaryTypeForColumn (
    tableDefinitionId: number,
    columnName: string,
    newType: string,
    options?: PrimaryTypeChangeOptions
  ): Promise<PrimaryTypeChangeResponse> {
    const request: PrimaryTypeChangeRequest = {
      newType,
      options
    };
    return await changePrimaryType(tableDefinitionId, columnName, request);
  }


  // Konstanter
  const nullHandlingOptions = [
    { value: 'allow', label: 'Tillat tomme verdier' },
    { value: 'required', label: 'Påkrevd felt (uten standardverdi)' },
    { value: 'default', label: 'Ikke tomt, bruk standardverdi' }
  ];

  const separatorOptions = [
    { value: ',', label: 'Komma (,)' },
    { value: ';', label: 'Semikolon (;)' },
    { value: '|', label: 'Pipe (|)' },
    { value: '\t', label: 'Tab' }
  ];


  // Helper-metoder for nullHandling
  function getNullHandlingLabel (nullHandling: string): string {
    const option = nullHandlingOptions.find(opt => opt.value === nullHandling);
    return option?.label || nullHandling;
  }

  function getNullHandlingOptions (): Array<{ label: string, value: string }> {
    return nullHandlingOptions;
  }

  // Helper-metoder for defaultValue (moved to outer scope)
  const shouldShowDefaultValue = (nullHandling: string): boolean => {
    return nullHandling === 'default';
  };

  const validateDefaultValue = (defaultValue: string | null, nullHandling: string, design: FieldDesignDto | null): { valid: boolean, error?: string } => {
    if (nullHandling === 'default') {
      if (!defaultValue || (typeof defaultValue === 'string' && defaultValue.trim() === '')) {
        return { valid: false, error: 'Standardverdi må settes' };
      }
    }
    return { valid: true };
  };

  // Funksjon for å nullstille cache (nyttig for testing eller ved logout)
  function clearCache () {
    editComponentsLoaded.value = false;
    baseDataTypesLoaded.value = false;
    editComponentsPromise = null;
    baseDataTypesPromise = null;
    componentEditMappingPromise = null;
    editComponents.value = {};
    baseDataTypes.value = {};
    primaryTypes.value = [];
    componentEditMapping.value = [];
    errorEditComponents.value = null;
    errorBaseDataTypes.value = null;
  }

  return {
    editComponents,
    baseDataTypes,
    primaryTypes,
    componentEditMapping,
    loadingEditComponents,
    loadingBaseDataTypes,
    errorEditComponents,
    errorBaseDataTypes,
    editComponentsLoaded,
    baseDataTypesLoaded,
    formattedBaseDataTypes,
    formattedComponentEditList,
    transformedComponentEditList,
    nullHandlingOptions,
    separatorOptions,
    fetchEditComponents,
    fetchBaseDataTypes,
    fetchPrimaryTypes,
    fetchComponentEditMapping,
    resolvePrimary,
    changePrimaryTypeForColumn,
    needsMaxLength,
    needsPrecisionScale,
    needsNumberFormatting: (design: FieldDesignDto | null) => needsNumberFormatting(design),
    getParameterDefault,
    getParameterRange,
    getParameter,
    getNullHandlingLabel,
    getNullHandlingOptions,
    shouldShowDefaultValue,
    validateDefaultValue,
    clearCache
  };
});

// Export helper functions for use outside store
export {
  hasParameter,
  getParameter,
  needsNumberFormatting,
  needsMaxLength,
  needsPrecisionScale,
  getParameterDefault,
  getParameterRange
};
